import React, { useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Unstable_Grid2";
import Tooltip from "@mui/material/Tooltip";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { reloadPlannedWorkShifts } from "../../store/workShiftPlanningSlice";
import { useTranslation } from "react-i18next";
import {
    PlannedWorkShiftDto,
    PlannedWorkShiftState,
} from "../../models/plannedWorkShift/plannedWorkShift";
import { updatePlannedWorkShiftsState } from "../../services/plannedWorkShiftService";
import { IApiError } from "../../services/baseService";
import {
    showErrorMessage,
    showSuccessMessage,
} from "../../models/store/storeActions";
import WorkShiftHelpPopover from "./WorkShiftHelpPopover";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { usePublishShifts } from "../../hooks/workShiftPlanning/usePublishShifts";
import { selectColorSchemeByScope } from "../../store/colorSchemeSlice";
import { shiftColors } from "./utils/ShiftColors";

interface WorkShiftPlanningActionsProps {
    employees: EmployeeListItemDto[];
}

export const WorkShiftPlanningActions = ({
    employees,
}: WorkShiftPlanningActionsProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [savingState, setSavingState] = useState(false);

    const { publishAllShifts, saving: publishSaving } = usePublishShifts();

    const selectedEmployeeGroupId = useAppSelector(
        (state) => state.workShiftPlanning.filters.selectedEmployeeGroupId
    );

    const selectedEmployeeIds = useAppSelector(
        (state) => state?.workShiftPlanning?.filters?.selectedEmployeeIds
    );
    const isEmployeeSelectionOn = !!selectedEmployeeIds && selectedEmployeeIds.length > 0;

    const shifts = useAppSelector(
        (state) => state.workShiftPlanning.plannedWorkShifts
    );

    // Only consider shifts with a valid employeeId for publish/cancel
    const publishedShifts = useMemo(
        () =>
            shifts?.filter(
                s =>
                    s.state === PlannedWorkShiftState.Published &&
                    s.employeeId !== null
            ) || [],
        [shifts]
    );

    const planningShifts = useMemo(
        () =>
            shifts?.filter(
                s =>
                    s.state === PlannedWorkShiftState.InPlanning &&
                    s.employeeId !== null
            ) || [],
        [shifts]
    );

    const publishButtonText = useMemo(() => {
        if (isEmployeeSelectionOn) {
            return (publishedShifts?.length > 0 && planningShifts?.length > 0)
                ? t("workShiftPlanning.publishChanges")
                : t("workShiftPlanning.publishEntityShifts");
        }

        if (publishedShifts?.length > 0 && planningShifts?.length > 0) {
            return !!selectedEmployeeGroupId
                ? t("workShiftPlanning.publishEmployeeGroupChanges")
                : t("workShiftPlanning.publishPeriodChanges");
        }
        return !!selectedEmployeeGroupId
            ? t("workShiftPlanning.publishEmployeeGroupShifts")
            : t("workShiftPlanning.publishPeriodShifts");
    }, [
        publishedShifts?.length,
        planningShifts?.length,
        t,
        selectedEmployeeGroupId,
        isEmployeeSelectionOn,
    ]);

    const cancelButtonText = useMemo(() => {
        if (isEmployeeSelectionOn) {
            return t("workShiftPlanning.cancelEntityPublishShifts");
        }
        return selectedEmployeeGroupId
            ? t("workShiftPlanning.cancelEmployeeGroupPublishShifts")
            : t("workShiftPlanning.cancelPublishShifts");
    }, [isEmployeeSelectionOn, selectedEmployeeGroupId, t]);

    const handleUpdateState = (
        shifts: PlannedWorkShiftDto[],
        state: PlannedWorkShiftState
    ) => {
        if (!savingState && shifts?.length > 0) {
            setSavingState(true);

            updatePlannedWorkShiftsState(
                shifts.map((s) => ({
                    id: s.id,
                    rowId: s.rowId,
                    state,
                }))
            )
                .then(() => {
                    dispatch(showSuccessMessage(t("common.saveSuccess")));
                    dispatch(reloadPlannedWorkShifts());
                })
                .catch((error: IApiError) => {
                    dispatch(
                        showErrorMessage(
                            error?.status === 409
                                ? t("common.saveConflict")
                                : t("common.saveFailed")
                        )
                    );
                })
                .finally(() => setSavingState(false));
        }
    };

    const handlePublishShifts = () => {
        void publishAllShifts(planningShifts);
    };

    const isSaving = savingState || publishSaving;

    const colorScheme = useAppSelector(selectColorSchemeByScope("workShiftPlanning"));
    const isColorCodingOn = !!colorScheme?.enabled && !!colorScheme?.mappings && Object.keys(colorScheme.mappings).length > 0;
    const publishBtnColor = isColorCodingOn ? "primary" : "success";
    const cancelBtnColor = isColorCodingOn ? "primary" : "warning";
    const customPublishSx = useMemo(() => {
        if (!isColorCodingOn) return undefined;
        const key = colorScheme?.defaultColorKey ?? "blue";
        const palette = shiftColors[key];
        if (!palette) return undefined;
        return {
            bgcolor: palette.dark,
            color: "primary.contrastText",
            "&:hover": {
                bgcolor: palette.light,
            },
        };
    }, [isColorCodingOn, colorScheme?.defaultColorKey]);

    const employeeOnlyNote = t("workShiftPlanning.tooltips.publishOnlyWithEmployee");
    const publishTooltip = isEmployeeSelectionOn
        ? ((publishedShifts?.length > 0 && planningShifts?.length > 0
            ? t("workShiftPlanning.tooltips.publishChangesSelectedWorkers")
            : t("workShiftPlanning.tooltips.publishSelectedWorkersShifts")) + `. ${employeeOnlyNote}`)
        : "";

    const cancelTooltip = isEmployeeSelectionOn
        ? t("workShiftPlanning.tooltips.returnSelectedWorkersShiftsToPlanning")
        : "";

    const nonSelectionPublishTooltip = !isEmployeeSelectionOn
        ? (((publishedShifts?.length > 0 && planningShifts?.length > 0
            ? (selectedEmployeeGroupId
                ? t("workShiftPlanning.tooltips.publishGroupChanges")
                : t("workShiftPlanning.tooltips.publishPeriodChanges"))
            : (selectedEmployeeGroupId
                ? t("workShiftPlanning.tooltips.publishGroupShifts")
                : t("workShiftPlanning.tooltips.publishPeriodShifts"))) + `. ${employeeOnlyNote}`))
        : "";

    const nonSelectionCancelTooltip = !isEmployeeSelectionOn
        ? (selectedEmployeeGroupId
            ? t("workShiftPlanning.tooltips.returnGroupShiftsToPlanning")
            : t("workShiftPlanning.tooltips.returnPeriodShiftsToPlanning"))
        : "";

    return (
        <>
            {publishedShifts?.length > 0 && (
                <Grid2 xs="auto">
                    {isEmployeeSelectionOn ? (
                        <Tooltip title={cancelTooltip}>
                            <span>
                                <Button
                                    size="small"
                                    variant="contained"
                                    disabled={isSaving}
                                    color={cancelBtnColor}
                                    onClick={() =>
                                        handleUpdateState(
                                            publishedShifts,
                                            PlannedWorkShiftState.InPlanning
                                        )
                                    }
                                >
                                    {cancelButtonText}
                                </Button>
                            </span>
                        </Tooltip>
                    ) : (
                        <Tooltip title={nonSelectionCancelTooltip}>
                            <span>
                                <Button
                                    size="small"
                                    variant="contained"
                                    disabled={isSaving}
                                    color={cancelBtnColor}
                                    onClick={() =>
                                        handleUpdateState(
                                            publishedShifts,
                                            PlannedWorkShiftState.InPlanning
                                        )
                                    }
                                >
                                    {cancelButtonText}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Grid2>
            )}
            {planningShifts?.length > 0 && (
                <Grid2 xs="auto">
                    {isEmployeeSelectionOn ? (
                        <Tooltip title={publishTooltip}>
                            <span>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color={publishBtnColor}
                                    sx={customPublishSx}
                                    disabled={isSaving || !employees}
                                    onClick={handlePublishShifts}
                                >
                                    {publishButtonText}
                                </Button>
                            </span>
                        </Tooltip>
                    ) : (
                        <Tooltip title={nonSelectionPublishTooltip}>
                            <span>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color={publishBtnColor}
                                    sx={customPublishSx}
                                    disabled={isSaving || !employees}
                                    onClick={handlePublishShifts}
                                >
                                    {publishButtonText}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Grid2>
            )}
            <WorkShiftHelpPopover />
        </>
    );
};